import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { Box, Button, Column, Popover, TextField, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { FilterChip } from '../FilterChip/FilterChip';
import { useSettingsChipTone } from '../settingsChipTone';
import { initialFieldValue, isEmptyPredicate, isExcludeMode, isRangeModeOnly, multiValuesFor, operatorFor, parseNumbers, predicateFromNumbers, replaceIndex, valueLabelFor } from './NetDaysChip.utils';
export const GetNetDaysChipTID = (parentTestId)=>getTestIds(parentTestId);
export const NetDaysChip = (param)=>{
    let { testId, predicate, onChange } = param;
    const { NetDaysChipTestIds } = useMemo(()=>GetNetDaysChipTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('pages.draftsDashboard.aiSearch');
    const tone = useSettingsChipTone();
    const [anchorEl, setAnchorEl] = useState(null);
    const [fieldValue, setFieldValue] = useState(initialFieldValue(predicate));
    const [validationError, setValidationError] = useState(null);
    const [segmentAnchorEl, setSegmentAnchorEl] = useState(null);
    const [segmentIndex, setSegmentIndex] = useState(null);
    const [segmentFieldValue, setSegmentFieldValue] = useState('');
    const [segmentValidationError, setSegmentValidationError] = useState(null);
    const multiValues = multiValuesFor(predicate);
    const openPopover = (event)=>{
        setFieldValue(initialFieldValue(predicate));
        setValidationError(null);
        setAnchorEl(event.currentTarget);
    };
    const closePopover = ()=>setAnchorEl(null);
    const apply = ()=>{
        const numbers = parseNumbers(fieldValue);
        if (numbers == null) {
            setValidationError(t.netDaysValidationError());
            return;
        }
        onChange(predicateFromNumbers(numbers, isExcludeMode(predicate)));
        closePopover();
    };
    const applyOnEnter = (event)=>{
        if (event.key !== 'Enter') return;
        event.preventDefault();
        apply();
    };
    const openSegmentPopover = (index, value)=>(event)=>{
            setSegmentIndex(index);
            setSegmentFieldValue(String(value));
            setSegmentValidationError(null);
            setSegmentAnchorEl(event.currentTarget);
        };
    const closeSegmentPopover = ()=>setSegmentAnchorEl(null);
    const applySegment = ()=>{
        if (multiValues == null || segmentIndex == null) return;
        const replacement = parseNumbers(segmentFieldValue);
        if (replacement == null) {
            setSegmentValidationError(t.netDaysValidationError());
            return;
        }
        const nextNumbers = replaceIndex(multiValues, segmentIndex, replacement);
        onChange(predicateFromNumbers(nextNumbers, isExcludeMode(predicate)));
        closeSegmentPopover();
    };
    const applySegmentOnEnter = (event)=>{
        if (event.key !== 'Enter') return;
        event.preventDefault();
        applySegment();
    };
    const valueSegments = multiValues === null || multiValues === void 0 ? void 0 : multiValues.map((value, index)=>({
            label: t.netDaysValueIs({
                value
            }),
            onClick: openSegmentPopover(index, value),
            biName: 'drafts_ai_search_chip_edit_net_days_segment'
        }));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FilterChip, {
                testId: testId,
                fieldLabel: isEmptyPredicate(predicate) ? '' : t.netDaysLabel(),
                operator: operatorFor(predicate, onChange, t),
                valueLabel: valueSegments == null ? valueLabelFor(predicate, t) : undefined,
                valueSegments: valueSegments,
                combinator: valueSegments == null ? undefined : t.orConnector(),
                onValueClick: valueSegments != null || isRangeModeOnly(predicate) ? undefined : openPopover,
                onRemove: ()=>onChange(undefined),
                removeBiName: "drafts_ai_search_chip_remove_net_days",
                tone: tone
            }),
            /*#__PURE__*/ _jsx(Popover, {
                testId: NetDaysChipTestIds.EditPopover(),
                open: anchorEl != null,
                anchorEl: anchorEl,
                onClose: closePopover,
                children: /*#__PURE__*/ _jsx(Box, {
                    padding: 12,
                    children: /*#__PURE__*/ _jsxs(Column, {
                        gap: 8,
                        children: [
                            /*#__PURE__*/ _jsx(TextField, {
                                testId: NetDaysChipTestIds.ValuesField(),
                                bi: {
                                    name: 'drafts_ai_search_chip_edit_net_days'
                                },
                                value: fieldValue,
                                onChange: (event)=>{
                                    setFieldValue(event.target.value);
                                    setValidationError(null);
                                },
                                suffix: t.netDaysSuffix(),
                                inputProps: {
                                    onKeyDown: applyOnEnter
                                }
                            }),
                            validationError != null && /*#__PURE__*/ _jsx(Typography, {
                                testId: NetDaysChipTestIds.ValidationError(),
                                skin: "error",
                                variant: "paragraph2",
                                children: validationError
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                testId: NetDaysChipTestIds.ApplyButton(),
                                bi: {
                                    name: 'drafts_ai_search_chip_apply_net_days'
                                },
                                onClick: apply,
                                children: t.applyButton()
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Popover, {
                testId: NetDaysChipTestIds.SegmentPopover(),
                open: segmentAnchorEl != null,
                anchorEl: segmentAnchorEl,
                onClose: closeSegmentPopover,
                children: /*#__PURE__*/ _jsx(Box, {
                    padding: 12,
                    children: /*#__PURE__*/ _jsxs(Column, {
                        gap: 8,
                        children: [
                            /*#__PURE__*/ _jsx(TextField, {
                                testId: NetDaysChipTestIds.SegmentField(),
                                bi: {
                                    name: 'drafts_ai_search_chip_edit_net_days_segment_field'
                                },
                                value: segmentFieldValue,
                                onChange: (event)=>{
                                    setSegmentFieldValue(event.target.value);
                                    setSegmentValidationError(null);
                                },
                                suffix: t.netDaysSuffix(),
                                inputProps: {
                                    onKeyDown: applySegmentOnEnter
                                }
                            }),
                            segmentValidationError != null && /*#__PURE__*/ _jsx(Typography, {
                                testId: NetDaysChipTestIds.SegmentValidationError(),
                                skin: "error",
                                variant: "paragraph2",
                                children: segmentValidationError
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                testId: NetDaysChipTestIds.SegmentApplyButton(),
                                bi: {
                                    name: 'drafts_ai_search_chip_apply_net_days_segment'
                                },
                                onClick: applySegment,
                                children: t.applyButton()
                            })
                        ]
                    })
                })
            })
        ]
    });
};
